import axios, { type AxiosError, type AxiosResponse } from "axios";
import { toast } from "sonner";
import authProvider from "src/providers/auth-provider";
import type { ErrorResponse } from "src/types";

const MAX_2FA_ATTEMPTS = 3;

const getRequestId = (error: AxiosError): string | undefined => {
	const id =
		error.response?.headers?.["x-request-id"] ||
		error.response?.headers?.["x-correlation-id"];
	return typeof id === "string" ? id : undefined;
};

const shortId = (id: string) => id.slice(0, 8);

const buildErrorDescription = (
	detail: string | undefined,
	requestId: string | undefined,
) => {
	if (detail && requestId) return `${detail} · Request ${shortId(requestId)}`;
	if (detail) return detail;
	if (requestId) return `Request ${shortId(requestId)}`;
	return undefined;
};

const retryAction = (error: AxiosError) => {
	if (!error.config) return undefined;
	return {
		label: "Retry",
		onClick: () => {
			void axios.request(error.config!);
		},
	};
};

const copyRequestIdAction = (requestId: string) => ({
	label: "Copy ID",
	onClick: () => {
		void navigator.clipboard?.writeText(requestId);
	},
});

const handle2FAChallenge = async (
	originalError: AxiosError,
): Promise<AxiosResponse> => {
	for (let attempt = 0; attempt < MAX_2FA_ATTEMPTS; attempt++) {
		try {
			const code = await show2FAModal();

			if (!originalError.config) {
				console.error("No config on original error", originalError);
				return Promise.reject(originalError);
			}

			originalError.config.headers["X-2FA-Code"] = code;
			return await axios.request(originalError.config);
		} catch (retryError: unknown) {
			console.error(`2FA attempt ${attempt + 1} failed:`, retryError);

			const err = retryError as {
				response?: { status?: number; data?: { status?: string } };
			};
			if (
				err.response?.status === 403 &&
				err.response?.data?.status === "invalid_code"
			) {
				const attemptsLeft = MAX_2FA_ATTEMPTS - attempt - 1;

				if (attemptsLeft > 0) {
					toast.error("Invalid verification code", {
						description: `${attemptsLeft} attempt${attemptsLeft > 1 ? "s" : ""} remaining. Please check your authenticator app.`,
					});
					continue;
				}

				toast.error("Maximum verification attempts exceeded", {
					description: "Please try again later.",
				});
			}

			return Promise.reject(retryError);
		}
	}

	const maxAttemptsError = new Error(
		"2FA verification cancelled or max attempts exceeded",
	);
	console.error(maxAttemptsError);
	return Promise.reject(maxAttemptsError);
};

const apiRequest = axios.create({
	baseURL: import.meta.env.VITE_BACKEND_HOST,
	headers: {
		"Content-Type": "application/json",
		"Cache-Control": "no-cache",
		Accept: "application/json",
	},
	withCredentials: true,
});

let pendingVerification: {
	resolve: (code: string) => void;
	reject: () => void;
} | null = null;

export const show2FAModal = (): Promise<string> => {
	return new Promise((resolve, reject) => {
		pendingVerification = { resolve, reject };
		window.dispatchEvent(
			new CustomEvent("show2FAModal", {
				detail: {
					title: "Verify Your Identity",
					description: "This action requires two-factor authentication",
				},
			}),
		);
	});
};

export const resolve2FAModal = (code: string) => {
	if (pendingVerification) {
		pendingVerification.resolve(code);
		pendingVerification = null;
	}
};

export const reject2FAModal = () => {
	if (pendingVerification) {
		pendingVerification.reject();
		pendingVerification = null;
	}
};

apiRequest.interceptors.response.use(undefined, async (error: AxiosError) => {
	if (!error.response) {
		console.error("Network error or no response:", error);
		toast.error("Network error", {
			description: "Please check your internet connection",
			duration: 8000,
			action: retryAction(error),
		});
		return Promise.reject(error);
	}

	const { status } = error.response;
	const requestId = getRequestId(error);
	console.error(
		`API Error [${status}]${requestId ? ` [${requestId}]` : ""}:`,
		error.response.data,
	);

	if (status === 400) {
		const response: ErrorResponse = error.response.data as ErrorResponse;

		if (!response?.errors) {
			return Promise.reject(error);
		}

		const errors = response.errors.length
			? response.errors.map((item) => item.message).join(", ")
			: "";

		const detail = errors
			? response.status === "validation_error"
				? `Validation error: ${errors}.`
				: `Got the following errors: ${errors}.`
			: "Validation error";

		toast.error(response.message, {
			description: buildErrorDescription(detail, requestId),
			action: requestId ? copyRequestIdAction(requestId) : undefined,
		});
	} else if (status === 403) {
		const responseData = error.response.data as {
			status?: string;
			message?: string;
			error?: string;
		};

		if (
			responseData?.status === "2fa_verification_required" ||
			responseData?.error === "2fa_verification_required"
		) {
			return handle2FAChallenge(error);
		}

		// CSRF token refresh
		try {
			const newToken = await authProvider.getCookie();

			if (error.config?.headers && newToken.length) {
				error.config.headers["x-csrf-token"] = newToken;
				return axios.request(error.config);
			}
		} catch (_) {
			// CSRF refresh failed -- fall through to rejection
		}
	} else if (status === 429) {
		toast.error("Too many requests", {
			description: buildErrorDescription(
				"Please slow down and try again in a moment",
				requestId,
			),
			duration: 8000,
			action: retryAction(error),
		});
	} else if (status >= 500) {
		toast.error("Server error", {
			description: buildErrorDescription(
				"Something went wrong on our end. Please try again.",
				requestId,
			),
			duration: 10000,
			action: retryAction(error),
		});
	} else if (status !== 401) {
		// 401 errors are handled by page components (redirect to login)
		toast.error("Something went wrong", {
			description: buildErrorDescription(error.message, requestId),
			action: requestId ? copyRequestIdAction(requestId) : undefined,
		});
	}

	return Promise.reject(error);
});

export default apiRequest;
